import { Injectable } from '@angular/core';
import { BehaviorSubject, tap, switchMap, Observable } from 'rxjs';
import { ClientSearchModel } from 'src/app/core/models/members/client/client-search-model';
import { ContactSearchModal } from 'src/app/core/models/contact/contacts-search.model';
import { SearchResultModel } from 'src/app/core/models/query/search-result-model';
import { RequisitionModel } from 'src/app/core/models/requisition/requisition.model';
import { MyfileApiService } from 'src/app/core/services/myfile-api.service';
import { GlobalComponent } from 'src/app/global-component';
import { RequisitionSearchModel } from '../../models/requisition/requisition-search.model';
import { TemplateDocumentType } from '../../models/document/document-template-model';
import { BaseListService } from './base-list.service';
import { LogicalOperator } from '../../enums/logical-operator.enum';
import { FilterInfoModel } from '../../models/security/filters-info.model';
import { ModelTypeEnum } from '../../enums/model-type-enum';
import { SearchBarExtendedFilterModel } from '../../models/table/searchbar-extended-filter.model';
import { UserSearchModel } from '../../models/members/user/user-search.model';

export enum RequisitionListColumnSearch {
  Number = "Number",
  Price = "Price",
  ModifiedDate = "ModifiedDate",
  Type = "Type",
  MatterNumber = "MatterNumber",
}

@Injectable({
  providedIn: 'root'
})
export class RequisitionListService extends BaseListService {

  defaultRequisitionListState: RequisitionSearchModel = {
    sortColumnDirective: 'modifiedDate',
    sortDirectionDirective: 'desc',
    createdById: undefined,
    pageSize: GlobalComponent.defaultPagingInfo.pageSize,
    pageIndex: 0,
    startIndex: 0,
    sortColumn: '',
    sortDirection: 'desc',
    searchTerm: undefined,
    status: undefined,
    clientLimitedSearch: undefined
  };

  private _resultItems$ = new BehaviorSubject<RequisitionModel[]>([]);

  protected override _state: RequisitionSearchModel = { ...this.defaultRequisitionListState };

  private resetSearchIds: boolean = false;

  constructor(
    private myFileApiService: MyfileApiService) {
    super();

    this._search$.pipe(
      tap(() => this._loading$.next(true)),
      switchMap(() => this._search()),
      tap(() => this._loading$.next(false))
    ).subscribe((result: any) => {
      this._resultItems$.next(result.items);
      this._total$.next(result.count);
    });
  }

  get resultItems$() { return this._resultItems$.asObservable(); }
  get status() { return this._state.status; }
  set status(status: any) { this._set({ status }); }
  set clientLimitedSearch(clientLimitedSearch: string | undefined) {
    this._state.clientLimitedSearch = clientLimitedSearch;
  }
  set createdById(createdById: any) { this._state.createdById = createdById }
  set assigneeIds(assigneeId: string | undefined) {
    this._state.assigneeIds = []
    if (assigneeId == undefined) {
      this._state.assigneeIds = undefined
    } else {
      this._state.assigneeIds.push(assigneeId);
    }
  }
  set reqId(id: string) { this._state.ids = [id]; this.resetSearchIds = true; }
  set documentTypes(type: TemplateDocumentType) { this._state.documentTypes = []; this._state.documentTypes.push(type) }

  clearListService() {
    this._state = { ...this.defaultRequisitionListState };
    this.clearSearchTermFilterOptions();
    this._resultItems$.next(new Array<RequisitionModel>());
    this._total$.next(0);
  }

  searchTermFilterOptions(FilterModelArray: SearchBarExtendedFilterModel[] | undefined, searchTerm: string | undefined, preformASearch: boolean) {
    this.clearSearchTermFilterOptions();
    if (FilterModelArray != undefined && searchTerm != undefined && searchTerm != "") {
      FilterModelArray.forEach((filterModel: SearchBarExtendedFilterModel) => {
        switch (filterModel.modelType) {
          case ModelTypeEnum.UserModel:
            if (this._state.filterInfo == undefined) {
              this._state.filterInfo = new FilterInfoModel();
            }
            this._state.filterInfo.logicalOperator = LogicalOperator.And;
            if (this._state.createdBySearch == undefined) {
              this._state.createdBySearch = new UserSearchModel();
            }
            if (filterModel.dataField == 'FullName') {
              this._state.createdBySearch.fullName = searchTerm;
            }

            break;
          case ModelTypeEnum.RequisitionModel:
            if (this._state.filterInfo == undefined) {
              this._state.filterInfo = new FilterInfoModel();
            }
            this._state.filterInfo.logicalOperator = LogicalOperator.And;

            if (filterModel.dataField == 'MatterNumber') {
              this._state.matterNumber = searchTerm;
            }
            if (filterModel.dataField == 'Number') {
              this._state.number = searchTerm;
            }
            if (filterModel.dataField == 'Type') {
              this._state.type = searchTerm;
            }
            break;
          case ModelTypeEnum.ClientModel:
            if (this._state.clientSearch == undefined) {
              this._state.clientSearch = new ClientSearchModel();
            }
            if (filterModel.dataField == 'Name') {
              this._state.clientSearch.name = searchTerm;
            }
            if (filterModel.dataField == 'FullName') {
              this._state.clientSearch.fullName = searchTerm;
            }
            if (filterModel.dataField == 'LastName') {
              this._state.clientSearch.lastName = searchTerm;
            }
            if (filterModel.dataField == 'Email') {
              this._state.clientSearch.email = searchTerm;
            }
            if (filterModel.dataField == 'PhoneNumber') {
              this._state.clientSearch.phoneNumber = searchTerm;
            }
            if (filterModel.dataField == 'MatterNumber') {
              this._state.clientSearch.matterNumber = searchTerm;
            }
            break;
          case ModelTypeEnum.ContactModel:
            if (this._state.accountSearch == undefined) {
              this._state.accountSearch = new ContactSearchModal();
            }
            if (filterModel.dataField == 'FirstName') {
              this._state.accountSearch.firstName = searchTerm;
            }
            if (filterModel.dataField == 'LastName') {
              this._state.accountSearch.lastName = searchTerm;
            }
            if (filterModel.dataField == 'CompanyName') {
              this._state.accountSearch.companyName = searchTerm;
            }
            if (filterModel.dataField == 'Email') {
              this._state.accountSearch.email = searchTerm;
            }
            if (filterModel.dataField == 'PhoneNumber') {
              this._state.accountSearch.phoneNumber = searchTerm;
            }
            if (filterModel.dataField == 'FaxNumber') {
              this._state.accountSearch.faxNumber = searchTerm;
            }
            break;
        }
      })
    }

    if (this._state.accountSearch != undefined) {
      if (this._state.accountSearch.filterInfo == undefined) {
        this._state.accountSearch.filterInfo = new FilterInfoModel();
      }
      this._state.accountSearch.filterInfo.logicalOperator = LogicalOperator.And;
    }

    if (this._state.clientSearch != undefined) {
      if (this._state.clientSearch.filterInfo == undefined) {
        this._state.clientSearch.filterInfo = new FilterInfoModel();
      }
      this._state.clientSearch.filterInfo.logicalOperator = LogicalOperator.And;
    }

    this.FilterModelArray = FilterModelArray;

    if (FilterModelArray == undefined || FilterModelArray.length == 0) {
      this.clearSearchTermFilterOptions();
    }

    if (preformASearch) {
      this._search$.next();
    }

  }

  clearSearchTermFilterOptions() {
    this._state.filterInfo = undefined;
    this._state.accountSearch = undefined;
    this._state.clientSearch = undefined;
    this._state.matterNumber = undefined;
    this._state.number = undefined;
    this._state.type = undefined;
  }

  protected _search(): Observable<SearchResultModel<RequisitionModel> | undefined> {

    this.validateStartIndex();

    if (!this._state.accountSearch) {
      this._state.accountSearch = new ContactSearchModal();
    }

    if (!this._state.clientSearch) {
      this._state.clientSearch = new ClientSearchModel();
    }

    if (this.FilterModelArray != undefined && this.FilterModelArray.length > 0) {
      this.searchTermFilterOptions(this.FilterModelArray, this._state.searchTerm, false)
    } else {
      if (this._state.searchTerm && this._state.searchTerm.length > 0) {
        this._state.clientSearch.searchTerm = this._state.searchTerm;
        this._state.accountSearch.searchTerm = this._state.searchTerm;
      } else {
        this._state.searchTerm = undefined;
        this._state.accountSearch = undefined;
        this._state.clientSearch = undefined;
      }
    }

    if (this._state.clientLimitedSearch) {
      this._state.clientSearch = new ClientSearchModel();
      this._state.clientSearch.ids = [this._state.clientLimitedSearch];
      this._state.clientSearch.filterInfo = new FilterInfoModel();
      this._state.clientSearch.filterInfo.logicalOperator = LogicalOperator.And;
    }

    this._state.sortColumn = this.titleCaseWord(this._state.sortColumnDirective) as string;

    var requestState = { ...this._state };

    if (this.resetSearchIds === true) {
      this._state.ids = undefined;
      this.resetSearchIds = false;
    }

    if (this.FilterModelArray != undefined && this.FilterModelArray.length > 0) {
      requestState.searchTerm = undefined;
      if (requestState.accountSearch != undefined) {
        requestState.accountSearch.searchTerm = undefined;
      }
      if (requestState.clientSearch != undefined) {
        requestState.clientSearch.searchTerm = undefined;
      }
    }

    return this.myFileApiService.getRequisitionList(requestState);
  }

}